import { useEffect, useState } from "react";
import { DateView } from "nystem-components";
import app from "nystem";

const ViewButtonEnergyNow = ({ model, view }) => {
  const [energy, setEnergy] = useState([]);
  const [consumption, setConsumption] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [start, setStart] = useState(() => new Date().setHours(0, 0, 0, 0));

  useEffect(() => {
    let mounted = true;
    const update = async () => {
      const { energy = [] } = await app.connection.emit({ type: model.event });
      if (!mounted) return;
      setEnergy(energy);
      view.setValue({ value: Object.fromEntries(model.fields.slice(0, 3).map((field, pos) => [field, energy[pos]])) });
      setStart(new Date().setHours(0, 0, 0, 0));
    };
    const timer = setInterval(update, 5000);
    update();
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [model.event, model.fields, view]);

  useEffect(() => {
    let mounted = true;
    let pending = false;
    setConsumption(null);
    setError("");
    const update = async () => {
      if (pending) return;
      pending = true;
      try {
        const { consumption, error } = await app.connection.emit({
          type: "energyConsumption",
          start,
          end: new Date(start).setDate(new Date(start).getDate() + 1),
        });
        if (mounted) {
          setConsumption(error ? null : consumption);
          setError(error || "");
        }
      } finally {
        pending = false;
      }
    };
    const timer = setInterval(update, 600000);
    update();
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [start]);

  const maximum = Math.max(0.01, ...(consumption || []).map(({ value }) => value));
  const active = consumption?.find(({ time }) => time === selected);
  const end = new Date(start).setDate(new Date(start).getDate() + 1);
  return (
    <section className="mt-3" aria-label="Shelly electricity consumption">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs text-gray-400">Shelly · Live power</p>
          <p className="mt-1 text-2xl font-semibold text-sky-300">
            {energy.length === 3 && energy.every(Number.isFinite)
              ? (energy.reduce((sum, power) => sum + power, 0) / 1000).toFixed(2)
              : "—"}
            <span className="ml-1 text-sm font-normal text-gray-400">kW</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Consumption today</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-300">
            {consumption ? consumption.reduce((sum, { value }) => sum + value, 0).toFixed(2) : "—"}
            <span className="ml-1 text-sm font-normal text-gray-400">kWh</span>
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 border-b border-gray-700 pb-3 text-sm">
        {["A", "B", "C"].map((phase, pos) => (
          <div key={phase} className="flex items-center justify-between gap-2">
            <span className="text-gray-400">Phase {phase}</span>
            <span>
              {Number.isFinite(energy[pos]) ? Math.round(energy[pos]) : "—"}{" "}
              <span className="text-xs text-gray-500">W</span>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
        <span>Today by hour · kWh</span>
        {active && (
          <span className="flex items-center gap-1">
            <DateView model={{ dateFormat: "time", renderAs: "span" }} value={active.time} /> ·{" "}
            {active.value.toFixed(2)} kWh
          </span>
        )}
      </div>
      {consumption ? (
        <>
          <div className="mt-2 flex h-28 items-end gap-1 border-b border-gray-700">
            {Array.from({ length: Math.round((end - start) / 3600000) }, (_, hour) => {
              const time = start + hour * 3600000;
              const reading = consumption.find((reading) => reading.time === time);
              return (
                <button
                  key={time}
                  type="button"
                  disabled={!reading}
                  aria-label={reading ? `${reading.value.toFixed(2)} kWh, hour ${hour + 1}` : "No recorded consumption"}
                  className={`min-w-0 flex-1 rounded-t-sm focus:outline-2 focus:outline-white ${reading ? (selected === time ? "bg-emerald-300" : "bg-emerald-500/80 hover:bg-emerald-300") : "bg-gray-700/40"}`}
                  style={{ height: reading ? `${Math.max(2, (reading.value / maximum) * 100)}%` : "2px" }}
                  onMouseEnter={() => setSelected(time)}
                  onFocus={() => setSelected(time)}
                  onClick={() => setSelected(time)}
                />
              );
            })}
          </div>
          <div className="relative mt-1 h-5 text-[10px] text-gray-400">
            {[0, 6, 12, 18].map((hour) => (
              <span key={hour} className="absolute" style={{ left: `${(hour / ((end - start) / 3600000)) * 100}%` }}>
                <DateView model={{ dateFormat: "time", renderAs: "span" }} value={start + hour * 3600000} />
              </span>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-gray-500">Recorded consumption · Current hour may be incomplete.</p>
        </>
      ) : (
        <p className="py-3 text-xs text-gray-400">{error || "Loading today’s Shelly history…"}</p>
      )}
    </section>
  );
};

export default ViewButtonEnergyNow;
